import { cn } from "@/lib/utils";
import React, { useEffect, useRef } from "react";

interface DocumentNameInlineEditProps {
  /** Editable basename (without extension) */
  basename: string;
  /** Read-only file extension including the leading dot, e.g. ".pdf". Empty if none. */
  extension: string;
  /** Called as the user types (basename only) */
  onChange: (value: string) => void;
  /** Called on Enter or blur */
  onConfirm: () => void;
  /** Called on Escape */
  onCancel: () => void;
  /** Additional classes for the wrapper */
  className?: string;
  /** aria-label for the input */
  ariaLabel?: string;
}

/**
 * Inline edit for a document filename. Shows the basename inside an input and
 * the extension as a read-only span next to it. The extension is preserved
 * outside the editable surface so the user cannot change the file type by
 * accident — matching Finder/Explorer rename behavior.
 *
 * Selects the entire basename when mounted so the user can replace immediately.
 */
export const DocumentNameInlineEdit = React.memo(function DocumentNameInlineEdit({
  basename,
  extension,
  onChange,
  onConfirm,
  onCancel,
  className,
  ariaLabel,
}: DocumentNameInlineEditProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onConfirm();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div
      className={cn("flex min-w-0 items-center gap-0.5", className)}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        type="text"
        value={basename}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={onConfirm}
        className={cn(
          "min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-2 py-0.5 text-sm font-medium text-slate-900",
          "shadow-subtle transition-all duration-150",
          "focus-visible:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
        )}
        aria-label={ariaLabel ?? "Edit document name"}
      />
      {extension && (
        <span
          className="flex-shrink-0 select-none px-1 text-sm font-medium text-muted-foreground"
          aria-hidden="true"
        >
          {extension}
        </span>
      )}
    </div>
  );
});
